import { 
  TextField, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Checkbox
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { AppDispatch, RootState } from '../../../redux/store'
import { createSetupTemplate, updateSetupTemplate } from '../../../redux/notificationCenter'
import { NotificationMessageTemplate, NotificationSetupTemplate } from '../../../services/Hub/notificationCenter/types'
import { Notifications } from '../../../services/Hub/notificationCenter/notificationCenterApi'
import { AdvancedExpressionBuilder } from '../../../shared/components/AdvancedExpressionBuilder'
import { ASTQueryBuilder } from '../../../shared/components/ASTQueryBuilder'
import { FullScreenModal } from '../../../shared/components/FullScreenModal'
import {
  ExpressionGroupsState,
  FieldDefinition,
  SelectOption,
  ApiConditions
} from '../../../shared/components/AdvancedExpressionBuilder/types'
import { RESOURCE } from '../../../services/Hub/Authorization/authorization.constants'
import { buildConditionDefinition } from '../../../shared/components/AdvancedExpressionBuilder/utils/buildConditionDefinition'
import { transformConditionsToExpressionGroupsState } from '../../../shared/components/AdvancedExpressionBuilder/utils/transformConditionsToExpressionGroupsState'
import { mongoQueryToRuleGroup } from '../../../shared/components/ASTQueryBuilder/utils/mongoQueryToRuleGroup'
import { RuleGroupType } from 'react-querybuilder'

type Props = {
  open: boolean
  onClose: () => void
  fetchFieldCatalog: (search?: string) => Promise<FieldDefinition[]>
  fetchResourceValues: (resourceId: string, search?: string) => Promise<SelectOption[]>
  messageTemplates?: NotificationMessageTemplate[]
  setupTemplates?: NotificationSetupTemplate[]
  template?: NotificationSetupTemplate | null
  organizationId?: string // Opcional: se não fornecido, usa mock
}

interface UpsertFormData {
  name: string
  description: string
  source: string
  notificationMessageTemplateId: string
  applyConditions: boolean
  staticRecipients: string
  contextualRecipients: string
}

const initialFormData: UpsertFormData = {
  name: '',
  description: '',
  source: '',
  notificationMessageTemplateId: '',
  applyConditions: false,
  staticRecipients: '',
  contextualRecipients: ''
}

export default function UpsertSetupTemplateModal({
  open,
  onClose,
  fetchFieldCatalog,
  fetchResourceValues,
  messageTemplates = [],
  setupTemplates = [],
  template = null,
  organizationId
}: Props) {
  const dispatch: AppDispatch = useDispatch()
  
  const [formData, setFormData] = useState<UpsertFormData>(initialFormData)
  const [expressions, setExpressions] = useState<ExpressionGroupsState | null>(null)
  const [mongoQuery, setMongoQuery] = useState<RuleGroupType | null>(null)
  const [mongoQueryOutput, setMongoQueryOutput] = useState<Record<string, any> | null>(null)
  const [useNewEngine, setUseNewEngine] = useState(true) // Default to new engine for new notifications
  const [triggerSources, setTriggerSources] = useState<string[]>([])
  const [displayableMessageTemplates, setDisplayableMessageTemplates] = useState<NotificationMessageTemplate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  const isEditMode = template !== null;

  // When editing, wait for detailed template data before showing the expression builder
  useEffect(() => {
    if (isEditMode && template && open) {
      // Check if we have mongoQuery (new engine)
      if ((template as any).mongoQuery) {
        setUseNewEngine(true);
        const ruleGroup = mongoQueryToRuleGroup((template as any).mongoQuery);
        setMongoQuery(ruleGroup);
        setFormData(prev => ({
          ...prev,
          applyConditions: true
        }));
        setIsLoadingDetails(false);
      }
      // Check if we already have conditions data (legacy engine)
      else if (template.conditions) {
        setUseNewEngine(false);
        // We have detailed data, process it immediately
        const transformedExpressions = transformConditionsToExpressionGroupsState(template.conditions as any as ApiConditions);
        
        setExpressions(transformedExpressions || null);
        setFormData(prev => ({
          ...prev,
          applyConditions: !!(template.conditions && transformedExpressions)
        }));
        setIsLoadingDetails(false);
      } else {
        // We need to fetch detailed data
        setIsLoadingDetails(true);
        // The CanonicalNotificationsTab already calls fetchSetupTemplateDetails
        // We just need to wait for it to update the setupTemplates array
      }
    } else if (!isEditMode) {
      setUseNewEngine(true); // Default to new engine for new notifications
      setIsLoadingDetails(false);
    }
  }, [template, open, isEditMode]);

  // Listen for updates to setupTemplates array to get the detailed template
  useEffect(() => {
    if (isEditMode && template && isLoadingDetails && setupTemplates.length > 0) {
      const detailedTemplate = setupTemplates.find(t => t.id === template.id);
      if (detailedTemplate) {
        if ((detailedTemplate as any).mongoQuery) {
          setUseNewEngine(true);
          const ruleGroup = mongoQueryToRuleGroup((detailedTemplate as any).mongoQuery);
          setMongoQuery(ruleGroup);
          setFormData(prev => ({
            ...prev,
            applyConditions: true
          }));
          setIsLoadingDetails(false);
        } else if (detailedTemplate.conditions) {
          setUseNewEngine(false);
          const transformedExpressions = transformConditionsToExpressionGroupsState(detailedTemplate.conditions as any as ApiConditions);
          setExpressions(transformedExpressions || null);
          setFormData(prev => ({
            ...prev,
            applyConditions: !!(detailedTemplate.conditions && transformedExpressions)
          }));
          setIsLoadingDetails(false);
        }
      }
    }
  }, [setupTemplates, template, isEditMode, isLoadingDetails]);

  // Set initial form data when template changes
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        source: template.source || '',
        notificationMessageTemplateId: template.notificationMessageTemplateId || '',
        applyConditions: false, // Will be set to true later if conditions exist
        staticRecipients: template.recipientUserIds?.static?.join(', ') || '',
        contextualRecipients: template.recipientUserIds?.contextual?.join(', ') || ''
      });
    } else {
      setFormData(initialFormData);
      setExpressions(null);
    }
  }, [template]);

  // Load initial data when modal opens
  useEffect(() => {
    if (open) {
      loadInitialData()
    }
  }, [open])

  const loadInitialData = async () => {
    try {
      setLoadingData(true)
      
      // Extract trigger sources from existing setup templates
      const sourcesFromTemplates = [...new Set(
        setupTemplates
          .map(template => template.source)
          .filter(Boolean)
      )]
      
      // Try to load additional trigger sources from API
      let apiSources: string[] = []
      try {
        const triggerData = await Notifications.triggers.sources()
        apiSources = [...new Set([
          ...triggerData.eventsByTriggerSources.map((item: any) => item.triggerSource || ''),
          ...triggerData.triggerSourcesByEvents.map((item: any) => item.event || '')
        ])].filter(Boolean);
      } catch (error) {
        console.warn('Could not fetch trigger sources from API:', error);
      }
      
      // Get all known trigger sources (hardcoded list)
      const knownTriggerSources = [
        'TASK_CREATED',
        'TASK_UPDATED', 
        'TASK_COMPLETED',
        'TICKET_CREATED',
        'TICKET_UPDATED',
        'TICKET_TRANSITION_CREATED',
        'TICKET_COMMENT_CREATED',
        'DOCUMENT_FORMALIZATION_TICKET_CREATED',
        'DOCUMENT_FORMALIZATION_TICKET_UPDATED',
        'DOCUMENT_FORMALIZATION_TICKET_TRANSITION_CREATED',
        'DOCUMENT_SIGNATURE_COMPLETED',
        'DOCUMENT_SIGNATURE_CANCELED',
        'COLLATERAL_ASSETS_CREATED',
        'COLLATERAL_ASSETS_UPDATED',
        'AGMT_DOCUMENT_REGISTER_STATUS_CHANGED'
      ]
      
      // Combine all sources
      const allSources = [...new Set([
        ...sourcesFromTemplates, 
        ...apiSources, 
        ...knownTriggerSources
      ])].sort()
      
      setTriggerSources(allSources)
      
      // Filter global message templates (no ownerOrganizationId)
      const globals = messageTemplates.filter(template => !template.ownerOrganizationId)
      
      if (isEditMode && template?.notificationMessageTemplateId) {
        const isSelectedTemplateInList = globals.some(t => t.id === template.notificationMessageTemplateId);
        if (!isSelectedTemplateInList) {
          const selectedTemplateFromAll = messageTemplates.find(t => t.id === template.notificationMessageTemplateId);
          if (selectedTemplateFromAll) {
            setDisplayableMessageTemplates([...globals, selectedTemplateFromAll]);
          } else {
            setDisplayableMessageTemplates(globals);
          }
        } else {
          setDisplayableMessageTemplates(globals);
        }
      } else {
        setDisplayableMessageTemplates(globals);
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error)
    } finally {
      setLoadingData(false)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }
    
    if (!formData.source) {
      newErrors.source = 'Fonte do trigger é obrigatória'
    }
    
    if (!formData.notificationMessageTemplateId) {
      newErrors.notificationMessageTemplateId = 'Template de mensagem é obrigatório'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }
    
    try {
      setIsLoading(true)
      
      // Prepare recipient data
      const recipientUserIds: any = {}
      if (formData.staticRecipients.trim()) {
        recipientUserIds.static = formData.staticRecipients
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
      }
      if (formData.contextualRecipients.trim()) {
        recipientUserIds.contextual = formData.contextualRecipients
          .split(',')
          .map(path => path.trim())
          .filter(Boolean)
      }
      
      // Prepare submission data based on the engine being used
      let submissionData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        source: formData.source,
        notificationMessageTemplateId: formData.notificationMessageTemplateId,
        recipientUserIds: Object.keys(recipientUserIds).length > 0 ? recipientUserIds : undefined
      };

      // Add conditions based on which engine is being used
      if (formData.applyConditions) {
        if (useNewEngine) {
          // MongoDB query format - this will be saved in the mongoQuery field
          submissionData.mongoQuery = mongoQueryOutput;
        } else {
          // Legacy conditions format
          const transformedConditions = buildConditionDefinition(expressions);
          if (transformedConditions) {
            submissionData.conditions = {
              expressions: transformedConditions.expressions,
              expressionsStructure: transformedConditions.expressionsStructure,
            };
          }
        }
      }
      
      if (isEditMode) {
        dispatch(updateSetupTemplate({ ...submissionData, id: template.id } as NotificationSetupTemplate));
      } else {
        dispatch(createSetupTemplate(submissionData));
      }
      
      // Reset form and close
      setFormData(initialFormData)
      setExpressions(null)
      setErrors({})
      onClose()
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} notification:`, error)
      setErrors({ submit: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} notificação. Tente novamente.` })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setFormData(initialFormData)
    setExpressions(null)
    setErrors({})
    onClose()
  }

  const updateFormData = (field: keyof UpsertFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <FullScreenModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Editar Notificação Canônica" : "Nova Notificação Canônica"}
      subtitle={isEditMode ? formData.name : "Configure um template de notificação reutilizável"}
      primaryAction={{
        label: isEditMode ? 'Salvar Alterações' : 'Criar Notificação',
        onClick: handleSubmit,
        disabled: loadingData,
        loading: isLoading
      }}
    >
      {loadingData ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
            px: { xs: 2, md: 4 } // Better responsive margins
          }}
        >
          <Box 
            component="form" 
            sx={{ 
              maxWidth: '1200px', // Max width for very large screens
              width: '100%',
              mx: 'auto', // Center the form
              display: 'flex', 
              flexDirection: 'column', 
              gap: { xs: 3, md: 4 } // Responsive gaps
            }}
          >
            
            {/* Basic Information */}
            <Box sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e9ecef'
            }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                📝 Informações Básicas
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <TextField
                    fullWidth
                    required
                    label="Nome"
                    value={formData.name}
                    onChange={e => updateFormData('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="Ex: Notificar quando tarefa for criada"
                    size="small"
                  />
                </Box>
                
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Descrição"
                    value={formData.description}
                    onChange={e => updateFormData('description', e.target.value)}
                    placeholder="Descreva quando esta notificação deve ser disparada..."
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Trigger Configuration */}
            <Box sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: '#f0f7ff',
              borderRadius: 2,
              border: '1px solid #d1ecf1'
            }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                ⚙️ Configuração do Trigger
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <FormControl fullWidth required error={!!errors.source} size="small">
                  <InputLabel>Fonte do Trigger</InputLabel>
                  <Select
                    value={formData.source}
                    label="Fonte do Trigger"
                    onChange={e => updateFormData('source', e.target.value as string)}
                  >
                    {triggerSources.map(source => (
                      <MenuItem key={source} value={source}>
                        {source}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.source && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.source}
                    </Typography>
                  )}
                </FormControl>
                
                <FormControl fullWidth required error={!!errors.notificationMessageTemplateId} size="small">
                  <InputLabel>Template de Mensagem</InputLabel>
                  <Select
                    value={formData.notificationMessageTemplateId}
                    label="Template de Mensagem"
                    onChange={e => updateFormData('notificationMessageTemplateId', e.target.value as string)}
                  >
                    {displayableMessageTemplates.map(template => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          <Typography variant="body2">{template.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Canal: {template.channel}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.notificationMessageTemplateId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.notificationMessageTemplateId}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Recipients Configuration */}
            <Box sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: '#f8f5ff',
              borderRadius: 2,
              border: '1px solid #e1d5f7'
            }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 1, color: 'primary.main' }}>
                📝 Configuração de Destinatários
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Configure quem deve receber as notificações (opcional)
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <TextField
                  fullWidth
                  label="IDs de Usuários Estáticos"
                  value={formData.staticRecipients}
                  onChange={e => updateFormData('staticRecipients', e.target.value)}
                  placeholder="user-uuid-1, user-uuid-2, user-uuid-3"
                  helperText="Separe múltiplos IDs por vírgula"
                  size="small"
                />
                
                <TextField
                  fullWidth
                  label="Caminhos Contextuais"
                  value={formData.contextualRecipients}
                  onChange={e => updateFormData('contextualRecipients', e.target.value)}
                  placeholder="context.task.assignedUserId, context.customer.managerId"
                  helperText="Caminhos para usuários baseados no contexto do evento"
                  size="small"
                />
              </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Advanced Conditions */}
            <Box sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: '#fff8e1',
              borderRadius: 2,
              border: '1px solid #ffecb3'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  🔧 Condições Avançadas
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.applyConditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, applyConditions: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Aplicar condições"
                />
              </Box>
              
              {formData.applyConditions && (
                <>
                  {!isEditMode && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        Engine de condições:
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useNewEngine}
                            onChange={(e) => setUseNewEngine(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={useNewEngine ? "MongoDB (Novo)" : "Legado"}
                      />
                      {useNewEngine && (
                        <Typography variant="caption" color="success.main">
                          ✨ Recomendado
                        </Typography>
                      )}
                    </Box>
                  )}
                  {isLoadingDetails ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Carregando condições existentes...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', pt: 2 }}>
                      {useNewEngine ? (
                        <ASTQueryBuilder
                          key={`ast-${template?.id || 'new'}`}
                          initialQuery={mongoQuery || undefined}
                          onChange={(query) => setMongoQueryOutput(query)}
                          fields={[
                            // Common fields for all trigger sources
                            { identifier: 'id', label: 'ID', fieldType: 'text' },
                            { identifier: 'createdAt', label: 'Created Date', fieldType: 'date' },
                            { identifier: 'updatedAt', label: 'Updated Date', fieldType: 'date' },
                            { identifier: 'status', label: 'Status', fieldType: 'select' },
                            { identifier: 'name', label: 'Name', fieldType: 'text' },
                            { identifier: 'description', label: 'Description', fieldType: 'text' },
                            { identifier: 'type', label: 'Type', fieldType: 'select' },
                            { identifier: 'value', label: 'Value', fieldType: 'numeric' },
                            { identifier: 'amount', label: 'Amount', fieldType: 'currency' },
                            { identifier: 'userId', label: 'User ID', fieldType: 'select', dynamicOptions: {
                              resource: RESOURCE['CRM:Memberships'],
                              mapValue: '{{user.id}}',
                              mapLabel: '{{user.name}} {{user.lastName}}',
                              groupBy: '{{user.role}}',
                            }},
                            { identifier: 'workflowId', label: 'Workflow ID', fieldType: 'select', dynamicOptions: {
                              resource: RESOURCE['CRM:Workflows'],
                              mapValue: '{{id}}',
                              mapLabel: '{{title}}',
                              groupBy: '{{type}}',
                            }},
                            { identifier: 'customerId', label: 'Customer ID', fieldType: 'select', dynamicOptions: {
                              resource: RESOURCE['CRM:Memberships'],
                              mapValue: '{{customer.id}}',
                              mapLabel: '{{customer.name}}',
                              groupBy: '{{customer.type}}',
                            }},
                            { identifier: 'ticketId', label: 'Ticket ID', fieldType: 'select', dynamicOptions: {
                              resource: RESOURCE['CRM:Workflows'],
                              mapValue: '{{id}}',
                              mapLabel: '{{title}}',
                              groupBy: '{{type}}',
                            }},
                          ]}
                          organizationId={organizationId || '3213'}
                          disabled={!formData.source}
                        />
                      ) : (
                        <AdvancedExpressionBuilder
                          key={`legacy-${template?.id || 'new'}`}
                          initialExpressions={expressions || undefined}
                          onChange={setExpressions}
                          fetchFieldCatalog={fetchFieldCatalog}
                          fetchResourceValues={fetchResourceValues}
                        />
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
            
            {errors.submit && (
              <Alert severity="error">
                {errors.submit}
              </Alert>
            )}
          </Box>
        </Box>
      )}
    </FullScreenModal>
  )
} 